// Tutorial definitions for the BKG module.
//
// The Companies landing page auto-starts a short guided tour (see Search.tsx);
// downstream views attach one-shot contextual hints via useTutorialHint. All
// ids are prefixed "bkg-" to match the meridian-*/ida-* conventions.

import type { TutorialHintDef, TutorialTourDef } from "../../components/Tutorial";

export const BKG_COMPANIES_TOUR_ID = "bkg-companies-intro";

export interface BkgCompaniesTourOptions {
  agentEnabled: boolean;
  canAddData: boolean;
  canViewQueue: boolean;
}

/**
 * The Companies landing-page tour. Role-gated steps are only included when the
 * target controls are mounted; otherwise the tour would point at elements that
 * are not available to the current user.
 */
export function buildBkgCompaniesTour({
  agentEnabled,
  canAddData,
  canViewQueue,
}: BkgCompaniesTourOptions): TutorialTourDef {
  const steps: TutorialHintDef[] = [
    {
      id: "bkg-tour-filters",
      targetId: "bkg-search-filters",
      title: "Search & filter companies",
      body: "Narrow the biotech universe by therapeutic area, development stage, technology and more. Combine filters to find exactly the companies you're after.",
      placement: "bottom",
    },
  ];

  if (canAddData) {
    steps.push({
      id: "bkg-tour-add-company",
      targetId: "bkg-add-company",
      title: "Add companies to the queue",
      body: "Add Company creates a seed item first. The indexer later enriches that item into full company, pipeline, leadership, investor, and event data before it appears in results.",
      placement: "bottom",
    });
  }

  steps.push({
    id: "bkg-tour-results",
    targetId: "bkg-search-results",
    title: "Browse your results",
    body: "Matching companies appear here. Click any company name to open its full profile: pipeline, leadership, and investors.",
    placement: "top",
  });

  if (canViewQueue) {
    steps.push({
      id: "bkg-tour-queue",
      targetId: "bkg-view-queue",
      title: "Track queue & indexing",
      body: "View Queue shows submitted seed items, scheduled indexer runs, and field enrichment progress. Use it when a newly added company has not appeared in search yet.",
      placement: "bottom",
    });
  }

  if (agentEnabled) {
    steps.push({
      id: "bkg-tour-ai",
      targetId: "bkg-ai-assistant",
      // The widget is pinned to the bottom-right corner; "top" keeps the tooltip
      // above it and fully inside the viewport.
      title: "Ask the AI assistant",
      body: "Have a question about a company, investor, or upcoming catalyst? Ask the AI assistant in plain language any time.",
      placement: "top",
    });
  }

  return { id: BKG_COMPANIES_TOUR_ID, steps };
}
